import { useAtom } from "jotai";
import { useState } from "react";
import classNames from "classnames";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { useCookies } from "react-cookie";
import Badge from "components/base/Badge";
import { UserInfo } from "interfaces/user";
import Avatar from "components/base/Avatar";
import FeatherIcon from "feather-icons-react";
import avatar from "assets/img/team/user.png";
import Scrollbar from "components/base/Scrollbar";
import { user_info } from "jotai/UserJotaiInstance";
import { Link, useNavigate } from "react-router-dom";
import { Card, Dropdown, Nav, Spinner } from "react-bootstrap";

type NavItem = {
  label: string;
  icon: string;
  link: string;
};

type ProfileDropdownMenuProps = {
  className?: string;
};

const ProfileDropdownMenu = ({ className }: ProfileDropdownMenuProps) => {
  const [userInfo] = useAtom<UserInfo>(user_info);
  const navigate = useNavigate();
  const [, setCookie] = useCookies(["isLoggedInKIQ"]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const [navItems] = useState<NavItem[]>([
    {
      label: "Security Settings",
      icon: "settings",
      link: "/settings/security-settings",
    },
  ]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      
      const response = await axiosInstance.post("/auth/logout");
      clearAuthData();
      
      toast.success(response.data.message);
      navigate("/sign-in");
    } catch (error: unknown) {      
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 'data' in error.response &&
          error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        // Ensure the message is treated as a string
        toast.error(String(error.response.data.message));
      } else {
        toast.error("Logout failed: Please try again later");
      }
      
      navigate("/sign-in");
    } finally {
      setIsLoggingOut(false);
    }
  };
  
  const clearAuthData = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    setCookie("isLoggedInKIQ", false);
  };

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        "navbar-top-dropdown-menu navbar-dropdown-caret py-0 dropdown-profile shadow border"
      )}
    >
      <Card className="position-relative border-0">
        <Card.Body className="p-0">
          {/* User Profile */}
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 pt-4 pb-3">
            <Avatar src={userInfo?.user_image || avatar} size="m" />
            <h5 className="text-body-emphasis">
              {userInfo?.user_first_name} {userInfo?.user_last_name}
            </h5>
            <h6 className="text-body-emphasis">{userInfo?.user_email}</h6>
            {userInfo?.user_subscription?.subscription_package?.subscription_package_name && (
              <Badge bg="primary">
                {userInfo.user_subscription.subscription_package.subscription_package_name}
              </Badge>
            )}
          </div>
          
          {/* Navigation Items */}
          <div className="nav-items-container">
            <Scrollbar>
              <Nav className="flex-column mb-2 pb-1">
                {navItems.map((item) => (
                  <Nav.Item key={item.label}>
                    <Nav.Link as={Link} to={item.link} className="px-3">
                      <FeatherIcon
                        icon={item.icon}
                        size={16}
                        className="me-2 text-body"
                      />
                      <span className="text-body-highlight">{item.label}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Scrollbar>
          </div>
        </Card.Body>
        
        <Card.Footer className="p-0 border-0">
          <hr className="my-0" />
          <div className="px-3 py-3">
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="btn btn-phoenix-secondary d-flex flex-center w-100"
              aria-label="Sign out"
            >
              {isLoggingOut ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  <span>Signing out...</span>
                </>
              ) : (
                <>
                  <FeatherIcon icon="log-out" className="me-2" size={16} />
                  <span>Sign out</span>
                </>
              )}
            </button>
          </div>
        </Card.Footer>
      </Card>
    </Dropdown.Menu>
  );
};
export default ProfileDropdownMenu;
